import { useState, useEffect } from "react";
import { useAuth } from "../../Context/AuthContext";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { IoHome } from "react-icons/io5";
import { FaEdit, FaTrash, FaChartBar } from "react-icons/fa";
import { toast } from "react-toastify";
import "./AdminUsers.css";

const API = process.env.REACT_APP_API;

const AdminUsers = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", phone: "", isAdmin: false });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserData, setEditingUserData] = useState({ name: "", email: "", phone: "", isAdmin: false });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) {
        toast.error("יש להתחבר למערכת");
        navigate("/login");
        return;
      }

      try {
        const res = await fetch(`${API}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // אם ה-token לא תקין
        if (res.status === 401) {
          toast.error("פג תוקף ההתחברות - יש להתחבר למערכת שוב");
          navigate("/login");
          return;
        }

        const data = await res.json();
        if (!res.ok) throw new Error((await res.json()).message || "תקלה בטעינת המשתמשים");
        setUsers(data);

      } catch (err) {
          console.error(err);
          toast.error("שגיאה: " + err.message);
          navigate("/login");
      }
    };
    fetchUsers();
  }, [token, navigate]);

  if (!user?.isAdmin) return <Navigate to="/login" replace />;

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!/^0\d{9}$/.test(newUser.phone)) {
      toast.error("מספר טלפון לא חוקי. חייב להיות 10 ספרות ומתחיל ב-0");
      return;
    }

    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("יש למלא את כל השדות החיוניים");
      return;
    }

    try {
      const res = await fetch(`${API}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "שגיאה ביצירת משתמש");

      const addedUser = {
        _id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        phone: newUser.phone,
        isAdmin: data.user.isAdmin
      };

      setUsers([...users, addedUser]);
      setNewUser({ name: "", email: "", password: "", phone: "", isAdmin: false });
      toast.success(`המשתמש ${data.user.name} נוצר בהצלחה`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    const userToDelete = users.find(u => u._id === id); // שמירה על המשתמש לפני המחיקה
    if (!window.confirm(`למחוק את המשתמש ${userToDelete.name}?`)) return;
    try {
      const res = await fetch(`${API}/api/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "שגיאה במחיקת משתמש");

      setUsers(users.filter(u => u._id !== id));
      toast.success(`המשתמש ${userToDelete.name} נמחק בהצלחה`);
    } catch (err) {
        toast.error(err.message || "שגיאה במחיקת משתמש");
    }
  };

  // Start editing
  const handleEditUser = (user) => {
    setEditingUserId(user._id);
    setEditingUserData({ name: user.name, email: user.email, phone: user.phone, isAdmin: user.isAdmin });
  };

  // Save edited user
  const handleSaveEdit = async (e) => {
    e.preventDefault();

    if (!/^0\d{9}$/.test(editingUserData.phone)) {
      toast.error("מספר טלפון לא חוקי. חייב להיות 10 ספרות ומתחיל ב-0");
      return;
    }

    try {
      const { data } = await axios.put(
      `${API}/api/users/${editingUserId}`,
      editingUserData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
      );

      setUsers(users.map(u => u._id === editingUserId ? { ...u, ...editingUserData } : u));
      setEditingUserId(null);
      toast.success(`המשתמש ${data.name} עודכן בהצלחה`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!user?.isAdmin) return <p>אין הרשאה</p>;

  return (
    <div className="admin-users-wrapper">
        <div className="home-icon" onClick={() => window.location.href = "/"}>
          <IoHome title="עמוד הבית" size={30} />
        </div>
        <div className="stats-icon" onClick={() => navigate("/admin/stats")}>
          <FaChartBar title="סטטיסטיקת כניסות" size={30}
          />
        </div>
      <div className="title">
        <span>משתמשים במערכת</span>
      </div>

      {/* ✅ עטיפת הטבלה בגלילה אופקית */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>שם</th>
              <th>Email</th>
              <th>טלפון</th>
              <th>מנהל?</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>{u.isAdmin ? "כן" : "לא"}</td>
                <td>
                  <FaEdit className="action-icon" onClick={() => handleEditUser(u)} title="עריכה" />
                  {u._id !== user._id && (
                    <FaTrash className="action-icon delete" onClick={() => handleDeleteUser(u._id)} title="מחיקה" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit form */}
      {editingUserId && (
        <div className="modal-overlay">
          <div className="edit-user-modal">
            <h2>עריכת משתמש</h2>
            <form onSubmit={handleSaveEdit}>
              <label>שם מלא:</label>
              <input
                type="text"
                value={editingUserData.name}
                onChange={e => setEditingUserData({ ...editingUserData, name: e.target.value })}
                required
              />

              <label>דוא״ל:</label>
              <input
                type="email"
                value={editingUserData.email}
                onChange={e => setEditingUserData({ ...editingUserData, email: e.target.value })}
                required
              />

              <label>טלפון:</label>
              <input
                type="text"
                value={editingUserData.phone}
                onChange={e => {
                const val = e.target.value.replace(/\D/g, ""); // מסיר כל דבר שלא מספר
                  if (val.length <= 10) setEditingUserData({ ...editingUserData, phone: val });
                }}
                placeholder="0501234567"
              />

              <label>סיסמה חדשה (רשות):</label>
              <input
                type="password"
                placeholder="השאר ריק אם לא משנה סיסמה"
                value={editingUserData.password || ""}
                onChange={e => setEditingUserData({ ...editingUserData, password: e.target.value })}
              />

              <label>סוג משתמש</label>
              <select
                value={editingUserData.isAdmin}
                onChange={e => setEditingUserData({ ...editingUserData, isAdmin: e.target.value === "true" })}
              >
                <option value={false}>רגיל</option>
                <option value={true}>מנהל</option>
              </select>

              <div className="modal-buttons">
                <button type="submit">שמירת שינויים</button>
                <button type="button" onClick={() => setEditingUserId(null)}>ביטול</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add new user */}
      <h2>הוסף משתמש חדש</h2>
      <form onSubmit={handleAddUser} className="add-user-form">
        <input type="text" placeholder="שם" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required />
        <input type="email" placeholder="דוא״ל" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
        <input type="password" placeholder="סיסמה" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
        <input type="text" placeholder="טלפון" value={newUser.phone} onChange={e => {
                                                                        const val = e.target.value.replace(/\D/g, "");
                                                                        if (val.length <= 10) setNewUser({ ...newUser, phone: val });
                                                                      }}
        required />
        <select value={newUser.isAdmin} onChange={e => setNewUser({ ...newUser, isAdmin: e.target.value === "true" })} title="סוג משתמש">
          <option value={false}>רגיל</option>
          <option value={true}>מנהל</option>
        </select>
        <button type="submit">צור משתמש</button>
      </form>
    </div>
  );
};

export default AdminUsers;
